import {
  Grid,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Button,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import { Months } from "../../../../utils";
import useDelete from "../../../../api/useDelete";
import useUpdate from "../../../../api/useUpdate";
import EditEmployeeLeaveDialog from "./EditEmployeeLeaveDialog";
import { useTranslation } from "react-i18next";
import EmployeeLeavePrint from "./EmployeeLeavePrint";
import PrintIcon from "@mui/icons-material/Print";

const EmployeeLeaveList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [employee, setEmployee] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [currentLeave, setCurrentLeave] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const { data: employeeFetchData } = useFetchObjects(
    ["employee"],
    "users/",
    token
  );

  const { data, refetch, isLoading, isError } = useFetchObjects(
    ["employee-leave"],
    `employee-leave/?month=${month}&year=${year}&employee=${employee}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("employee-leave", token);
  const { handleUpdate } = useUpdate("employee-leave", token);

  useEffect(() => {
    refetch();
  }, [month, year, employee, refetch]);

  const handleEditClick = (leave) => {
    setCurrentLeave(leave);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentLeave(null);
  };

  const handleSaveChanges = (editedLeave) => {
    handleUpdate(editedLeave.id, editedLeave);
    handleCloseDialog();
  };

  const handlePrintClick = (item) => {
    setPrintData(item);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f7f9fc",
        padding: "2rem",
        borderRadius: "8px",
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Header Section */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold" color="primary">
          {t("hr.manageEmployeeLeaves")}
        </Typography>
      </Stack>

      <Box
        sx={{
          backgroundColor: "#fff",
          padding: "1.5rem",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          mb: 4,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel id="month-label">{t("hr.month")}</InputLabel>
            <Select
              size="small"
              labelId="month-label"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              label={t("hr.month")}
            >
              {Months.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="year-label">{t("hr.year")}</InputLabel>
            <Select
              size="small"
              labelId="year-label"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              label={t("hr.year")}
            >
              {Array.from(
                { length: 4 },
                (_, index) => new Date().getFullYear() - index
              ).map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="employee-label">{t("hr.employee")}</InputLabel>
            <Select
              size="small"
              labelId="employee-label"
              value={employee}
              onChange={(e) => setEmployee(e.target.value)}
              label={t("hr.employee")}
            >
              {employeeFetchData?.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {`${emp.first_name} ${emp.last_name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            onClick={() => refetch()}
            sx={{ height: "40px" }}
          >
            {t("hr.filter")}
          </Button>
        </Stack>
      </Box>

      {/* Table Section */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">{t("hr.errorFetchingData")}</Alert>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f2f2f2" }}>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("hr.month")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("hr.year")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("hr.employee")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("hr.days")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("hr.description")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("hr.dateCreated")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {t("hr.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {data?.map((leave) => (
                <TableRow key={leave.id}>
                  <TableCell align="center">{leave.month_display}</TableCell>
                  <TableCell align="center">{leave.year}</TableCell>
                  <TableCell align="center">{leave.employee_display}</TableCell>
                  <TableCell align="center">{leave.days}</TableCell>
                  <TableCell align="center">{leave.description}</TableCell>
                  <TableCell align="center">
                    {new Date(leave.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={1}
                      sx={{
                        "& .MuiIconButton-root": {
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          padding: "6px",
                          transition: "all 0.3s",
                          "&:hover": {
                            backgroundColor: "#e0e0e0",
                          },
                        },
                      }}
                    >
                      <IconButton
                        onClick={() => handleEditClick(leave)}
                        color="primary"
                        size="small"
                        sx={{ "&:hover": { color: "#1976d2" } }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(leave.id)}
                        color="error"
                        size="small"
                        sx={{ "&:hover": { color: "#d32f2f" } }}
                      >
                        <Delete />
                      </IconButton>
                      <IconButton
                        onClick={() => handlePrintClick(leave)}
                        color="secondary"
                        size="small"
                        sx={{ "&:hover": { color: "#9c27b0" } }}
                      >
                        <PrintIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      {currentLeave && (
        <EditEmployeeLeaveDialog
          open={openDialog}
          onClose={handleCloseDialog}
          evaluation={currentLeave}
          employees={employeeFetchData}
          onSave={handleSaveChanges}
        />
      )}

      {/* Print Content */}
      <div ref={printRef} style={{ display: "none" }}>
        <EmployeeLeavePrint leaves={data} t={t} />
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Box>
  );
};

export default EmployeeLeaveList;
